<script setup lang="ts">
import { useIsFetching } from '@tanstack/vue-query';
import { breakpointsTailwind } from '@vueuse/core';
import type { NavigationMenuItem } from '#ui/types';
import type { PublicMerchant } from '~~/models/Merchant';
import { useMerchantsQuery } from '~/queries/useMerchantsQuery';
import { useAppShake } from '~/composables/useAppShake';
import { useArray } from '~/composables/useArray';
import { useIntercom } from '~/composables/useIntercom';
import { useIsDemo } from '~/composables/useIsDemo';
import { useIsFeatureEnabled } from '~/composables/useIsFeatureEnabled';
import { useIsTaxConfirmationReviewer } from '~/composables/useIsTaxConfirmationReviewer';
import { useMerchantSlugMaybeStatic } from '~/composables/useMerchantSlugMaybeStatic';
import { isNavItem, type MaybeNavItem } from '~/utils/isNavItem';
import { useSidebarControl } from '~/composables/useSidebarControl';

const props = defineProps<{
	merchant: PublicMerchant;
}>();

const { t } = useI18n();

const route = useRoute();

const merchantSlugMaybeStatic = useMerchantSlugMaybeStatic(toRef(() => props.merchant.slug));

const navigation = computed<NavigationMenuItem[]>(() => {
	if (merchantSlugMaybeStatic.value) {
		const items: MaybeNavItem[] = [
			{
				label: t('topbar.links.dashboard'),
				icon: 'i-lucide-house',
				to: {
					name: 'MerchantDashboard',
					params: {
						merchantSlug: merchantSlugMaybeStatic.value,
					},
				},
			},
			{
				label: t('topbar.links.programmes'),
				icon: 'i-lucide-link',
				defaultOpen: route.matched.some((r) => r.name === 'MerchantProgrammes' || r.name === 'Programme'),
				children: [
					{
						label: t('topbar.links.yourProgrammes'),
						to: {
							name: 'MerchantProgrammesYours',
							params: {
								merchantSlug: merchantSlugMaybeStatic.value,
							},
						},
						exact: true,
					},
					{
						label: t('topbar.links.availableProgrammes'),
						to: {
							name: 'MerchantProgrammesAvailable',
							params: {
								merchantSlug: merchantSlugMaybeStatic.value,
							},
						},
						exact: true,
					},
					{
						label: t('topbar.links.yourProgrammeApplications'),
						to: {
							name: 'MerchantProgrammesApplications',
							params: {
								merchantSlug: merchantSlugMaybeStatic.value,
							},
						},
						exact: true,
					},
				],
			},
			useIsFeatureEnabled('paidEngagementCampaigns').value
				? {
					label: t('topbar.links.campaigns'),
					icon: 'i-lucide-gallery-vertical-end',
					defaultOpen: route.matched.some((r) => r.name === 'MerchantCampaignsOverview'),
					children: [
						{
							label: t('topbar.links.availableCampaigns'),
							to: {
								name: 'MerchantCampaigns',
								params: {
									merchantSlug: merchantSlugMaybeStatic.value,
								},
							},
							exact: true,
						},
						{
							label: t('topbar.links.submissions'),
							to: {
								name: 'MerchantSubmissions',
								params: {
									merchantSlug: merchantSlugMaybeStatic.value,
								},
							},
						},
					],
				}
				: {
					label: t('topbar.links.campaigns'),
					icon: 'i-lucide-gallery-vertical-end',
					badge: 'Coming soon',
					disabled: true,
				},
			useIsFeatureEnabled('productWidget').value && {
				label: t('topbar.links.productWidget'),
				icon: 'i-lucide-square-code',
				to: { name: 'MerchantProductWidgets', params: { merchantSlug: merchantSlugMaybeStatic.value } },
			},
			{
				label: t('topbar.links.payouts'),
				icon: 'i-lucide-banknote',
				to: {
					name: 'MerchantPayouts',
					params: {
						merchantSlug: merchantSlugMaybeStatic.value,
					},
				},
			},
			{
				label: t('topbar.links.conversions'),
				icon: 'i-lucide-receipt-euro',
				to: { name: 'MerchantConversionReport', params: { merchantSlug: merchantSlugMaybeStatic.value } },
			},
			{
				label: t('topbar.links.productReport'),
				icon: 'i-lucide-file-text',
				to: { name: 'AffiliateProductReport', params: { merchantSlug: merchantSlugMaybeStatic.value } },
			},
			{
				label: t('topbar.links.clickReport'),
				icon: 'i-lucide-mouse-pointer-click',
				to: { name: 'MerchantClickReport', params: { merchantSlug: merchantSlugMaybeStatic.value } },
			},
			{
				label: t('topbar.links.clickReportRealtime'),
				icon: 'i-lucide-activity',
				to: { name: 'MerchantClickReportRealtime', params: { merchantSlug: merchantSlugMaybeStatic.value } },
			},
		];

		return items.filter(isNavItem);
	}

	return [];
});

const { data: merchantsData } = useMerchantsQuery();
const merchants = useArray(merchantsData);

const hasAdminAccess = computed(() => merchants.value.some((m) => m.id === props.merchant.id && m.isAdmin));

const isTaxConfirmationReviewer = useIsTaxConfirmationReviewer();

const adminNavigation = computed<NavigationMenuItem[]>(() => {
	if (!hasAdminAccess.value) {
		return [];
	}

	if (merchantSlugMaybeStatic.value) {
		const isPaidEngagementCampaignsEnabled = useIsFeatureEnabled('paidEngagementCampaigns').value;
		const isProductWidgetEnabled = useIsFeatureEnabled('productWidget').value;

		const items: MaybeNavItem[] = [
			{
				type: 'label',
				label: 'Admin',
			},
			{
				label: t('topbar.links.dashboard'),
				to: { name: 'AdminMerchantDashboard', params: { merchantSlug: merchantSlugMaybeStatic.value } },
				icon: 'i-lucide-house',
			},
			isPaidEngagementCampaignsEnabled && {
				label: t('topbar.links.campaigns'),
				to: { name: 'AdminMerchantCampaignsActive', params: { merchantSlug: merchantSlugMaybeStatic.value } },
				icon: 'i-lucide-gallery-vertical-end',
			},
			isPaidEngagementCampaignsEnabled && {
				label: t('topbar.links.segments'),
				to: { name: 'merchantSlug-admin-segments', params: { merchantSlug: merchantSlugMaybeStatic.value } },
				icon: 'i-lucide-users-round',
			},
			!isPaidEngagementCampaignsEnabled && {
				label: t('topbar.links.campaigns'),
				icon: 'i-lucide-gallery-vertical-end',
				badge: 'Coming soon',
				disabled: true,
			},
			{
				label: t('topbar.links.applications'),
				to: { name: 'AdminMerchantApplications', params: { merchantSlug: merchantSlugMaybeStatic.value } },
				icon: 'i-lucide-clipboard-list',
			},
			isTaxConfirmationReviewer.value && {
				label: t('topbar.links.taxConfirmations'),
				to: { name: 'AdminMerchantTaxConfirmations', params: { merchantSlug: merchantSlugMaybeStatic.value } },
				icon: 'i-lucide-file-check',
			},
			{
				label: t('topbar.links.affiliates'),
				to: { name: 'AdminMerchantAffiliates', params: { merchantSlug: merchantSlugMaybeStatic.value } },
				icon: 'i-lucide-id-card',
			},
			{
				label: t('topbar.links.conversions'),
				icon: 'i-lucide-receipt-euro',
				to: { name: 'AdminMerchantConversionReport', params: { merchantSlug: merchantSlugMaybeStatic.value } },
				badge: {
					label: t('badge.new'),
					color: 'primary',
					variant: 'solid',
				},
			},
			{
				label: t('topbar.links.partnerReport'),
				to: { name: 'AdminMerchantPartnerReport', params: { merchantSlug: merchantSlugMaybeStatic.value } },
				icon: 'i-lucide-chart-column',
				badge: {
					label: t('badge.new'),
					color: 'primary',
					variant: 'solid',
				},
			},
			isProductWidgetEnabled && {
				label: t('topbar.links.domains'),
				to: { name: 'AdminMerchantDomains', params: { merchantSlug: merchantSlugMaybeStatic.value } },
				icon: 'i-lucide-globe',
			},
		];

		return items.filter(isNavItem);
	}

	return [];
});

const isFetching = useIsFetching();

const { update } = useIntercom();

const breakpoints = useBreakpoints(breakpointsTailwind);
const isDesktopBreakpoint = breakpoints.greaterOrEqual('lg');

const { sidebarOpenPreference, sidebarOpen } = useSidebarControl();

const updateSidebarCollapsedPreference = (open: boolean) => {
	if (isDesktopBreakpoint.value) {
		sidebarOpenPreference.value = open;
	}
};

watch(sidebarOpen, (open) => {
	if (!isDesktopBreakpoint.value) {
		// Mobile slideover: hide the Intercom launcher while it is open.
		update({ hide_default_launcher: open });
	}
});

watch(isDesktopBreakpoint, (isDesktop) => {
	if (isDesktop) {
		update({ hide_default_launcher: false });

		if (route.meta.isSidebarCollapsed) {
			sidebarOpen.value = false;
		}
	}
});

// Close the mobile slideover on navigation so it does not cover the new page.
const router = useRouter();

router.beforeEach((r) => {
	if (!isDesktopBreakpoint.value || r.meta.isSidebarCollapsed) {
		sidebarOpen.value = false;
	} else {
		sidebarOpen.value = sidebarOpenPreference.value;
	}
});

onMounted(() => {
	if (route.meta.isSidebarCollapsed) {
		sidebarOpen.value = false;
	}
});

const { isShaking, stop: stopShaking } = useAppShake();

const isDemo = useIsDemo();
</script>

<template>
	<div
		class="lg:shrink-0"
		@animationend="stopShaking"
	>
		<UContainer class="bg-primary fixed top-0 z-99999999 lg:hidden">
			<div class="flex h-16 justify-between">
				<div class="flex items-center pb-1 relative">
					<NuxtLink to="/">
						<div v-if="merchant">
							<UColorModeImage
								v-if="merchant.logoUrl"
								:light="merchant.logoUrl"
								:dark="merchant.darkModeLogoUrl || merchant.logoUrl"
								:alt="merchant.name"
								class="h-6 brightness-0 invert"
							/>
							<img
								v-else
								class="h-8 w-auto block brightness-0 invert"
								src="/clickfire_logo_amber_500.svg"
								alt="Clickfire"
							>
						</div>
					</NuxtLink>
					<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
					<UBadge
						v-if="isDemo"
						color="warning"
						class="ml-2"
					>
						Demo
					</UBadge>
				</div>
				<div class="flex lg:ml-6 gap-1.5">
					<div class="flex items-center lg:hidden">
						<!-- Mobile menu button: toggles the slideover -->
						<button
							class="relative inline-flex items-center justify-center rounded-md bg-transparent text-white focus:outline-hidden focus:ring-1 focus:ring-white focus:ring-offset-1 tranistion-all duration-20"
							@click="sidebarOpen = !sidebarOpen"
						>
							<span class="sr-only">{{ $t('accessibility.topbar.openMainMenu') }}</span>
							<HamburgerBars
								:active="sidebarOpen"
								class="text-white w-12 -m-1"
							/>
						</button>
					</div>
				</div>
			</div>
		</UContainer>
		<UProgress
			v-show="isFetching"
			size="xs"
			:ui="{ base: 'bg-transparent' }"
			class="absolute"
		/>
		<USidebar
			v-model:open="sidebarOpen"
			variant="floating"
			collapsible="icon"
			rail
			:ui="{
				root: '[--sidebar-width:18.75rem] xl:[--sidebar-width:20rem] [--sidebar-width-icon:5.9rem]',
				container: ['lg:p-6 xl:p-8', isShaking ? 'animate-shake' : ''],
				rail: 'lg:end-6 xl:end-8 lg:inset-y-7 xl:inset-y-9',
				inner: 'bg-default shadow',
				header: 'border-0 min-h-20 pt-2',
				body: 'overflow-x-hidden'
			}"
			@update:open="updateSidebarCollapsedPreference"
		>
			<template #header="{ state, close }">
				<NuxtLink
					to="/"
					class="flex flex-1 items-center justify-center gap-2 min-w-0"
				>
					<template v-if="merchant && merchant.logoUrl">
						<UColorModeImage
							v-if="state === 'expanded'"
							:light="merchant.logoUrl"
							:dark="merchant.darkModeLogoUrl || merchant.logoUrl"
							:alt="merchant.name"
							class="h-7 w-auto max-w-full"
						/>
						<img
							v-else
							class="h-8 w-8 object-contain"
							src="/clickfire_logo_amber_500.svg"
							alt="Clickfire"
						>
					</template>
					<img
						v-else
						class="h-8 w-auto"
						src="/clickfire_logo_amber_500.svg"
						alt="Clickfire"
					>
					<UBadge
						v-if="isDemo && state === 'expanded'"
						color="warning"
					>
						Demo
					</UBadge>
				</NuxtLink>
				<UButton
					v-if="!isDesktopBreakpoint"
					icon="i-lucide-x"
					color="neutral"
					variant="ghost"
					:aria-label="$t('button.close')"
					@click="close"
				/>
			</template>

			<template #default="{ state }">
				<UNavigationMenu
					:items="navigation"
					orientation="vertical"
					:collapsed="state === 'collapsed'"
					:tooltip="state === 'collapsed'"
					:popover="state === 'collapsed'"
					:ui="{
						item: 'gap-2',
						list: 'space-y-2',
						childList: 'space-y-1 mt-1',
						link: 'text-default font-bold data-active:before:bg-primary-100 dark:data-active:before:bg-primary-800',
						linkLeadingIcon: 'text-primary',
					}"
				/>

				<template v-if="hasAdminAccess">
					<USeparator />
					<UNavigationMenu
						:items="adminNavigation"
						orientation="vertical"
						:collapsed="state === 'collapsed'"
						:tooltip="state === 'collapsed'"
						:popover="state === 'collapsed'"
						:ui="{
							item: 'gap-2',
							list: 'space-y-2',
							childList: 'space-y-1 mt-1',
							link: 'text-default font-bold data-active:before:bg-primary-100 dark:data-active:before:bg-primary-800',
							linkLeadingIcon: 'text-primary',
						}"
					/>
				</template>
			</template>

			<template #footer="{ state }">
				<UserDropdown
					:collapsed="state === 'collapsed'"
					class="w-full"
				/>
			</template>
		</USidebar>
	</div>
</template>
